import React, { useEffect, useRef } from 'react'
import { isEmpty } from 'lodash-es'
import { useSelector } from 'react-redux'
import bowser from 'bowser'
import { intl } from 'lang/intl'

import { ErrorModalComponent } from '../../../sharedLegacy/components'
import { getIsCriticalErrorTitleHidden } from '../../selectors'
import WebinarApp from '../WebinarAppComponent/WebinarAppComponent'
import useWebinarAppState from '../../providers/WebinarAppStateProvider'
import {
  getIsClientSecondCam,
  getRole,
  useLiveId,
} from '../../../global/blocs/roomInfo'
import { SecondCamContainer } from '../SecondCamContainer/SecondCamContainer'
import {
  FatalErrorScreen,
  setFatalError,
  useHasFatalError,
} from '../../../FatalError/FatalError'
import { useFocusMode } from '../../../FocusMode/blocs/useFocusMode'
import { FocusMode } from '../../../FocusMode/FocusMode'

import './WebinarContainerComponent.scss'

const getLiveData = (state) => state.webinar.liveData
const getCriticalError = (state) => state.webinar.criticalError
const getCriticalErrorType = (state) => state.webinar.criticalErrorType

const WebinarContainerComponent = () => {
  const { error } = useWebinarAppState()
  const liveId = useLiveId()
  const isLiveDataLoaded = useRef(false)
  const { enabled: isFocusModeEnabled } = useFocusMode()

  const criticalError = useSelector(getCriticalError)
  const criticalErrorType = useSelector(getCriticalErrorType)
  const isCriticalErrorTitleHidden = useSelector(getIsCriticalErrorTitleHidden)
  const liveData = useSelector(getLiveData)

  const isSecondCam = getIsClientSecondCam()
  const role = getRole()

  const hasFatalError = useHasFatalError()

  useEffect(() => {
    /*
     * make sure we check this only once when liveData is loaded because eventually, at some point, when
     * the webinar is ended the status will turn into "previous"
     */
    if (isEmpty(liveData) || isLiveDataLoaded.current) {
      return
    }

    isLiveDataLoaded.current = true
    if (liveData.live_status === 'previous') {
      setFatalError('WEBINAR_FINISHED')
    }
  }, [liveData])

  if (hasFatalError) {
    return <FatalErrorScreen />
  }

  if (error) {
    return (
      <ErrorModalComponent
        title={intl.formatMessage({ defaultMessage: 'Error!' })}
        message={error.message}
      />
    )
  }

  if (criticalError) {
    return (
      <ErrorModalComponent
        message={criticalError}
        errorType={criticalErrorType}
        liveId={liveId}
        {...(isCriticalErrorTitleHidden && { title: null })}
      />
    )
  }

  if (isSecondCam) {
    return <SecondCamContainer />
  }

  if ((role === 'presenter' || role === 'producer') && bowser.mobile) {
    setFatalError('PRESENTER_PRODUCER_MOBILE_DEVICE_NOT_SUPPORTED')
    return null
  }

  if (isFocusModeEnabled) {
    return <FocusMode />
  }

  return <WebinarApp />
}

export default WebinarContainerComponent

export { WebinarContainerComponent }
